import React, { useState } from 'react';
import { Box, Link, TextField, Typography } from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import Button from '../components/Button';
import PageContainer from '../layouts/PageContainer';
import { useV2Session } from '../state/SessionProvider';
import { getCachedSession } from '../services/user';
import { pilotAuthLogin, pilotAuthRegister, pilotAuthValidateOtp } from '../services/auth';
import { trackGa4Login, trackGa4SignUp } from '../services/firebaseAnalytics';
import { V2ApiError } from '../services/client';

function resolveReturnPath(from) {
  if (!from || typeof from.pathname !== 'string') return '/v2';
  const p = from.pathname;
  if (!p.startsWith('/v2')) return '/v2';
  if (p.startsWith('/v2/auth')) return '/v2';
  // Profile triggers auth when logged out; after login, land on home instead of account
  if (p === '/v2/account' || p.startsWith('/v2/account/')) return '/v2';
  return p;
}

function normalizeMobile(raw) {
  return raw.replace(/\D/g, '').slice(-10);
}

function isValidIndianMobile(digits) {
  return /^[6789]\d{9}$/.test(digits);
}

export default function Auth() {
  const { loggedIn, login } = useV2Session();
  const location = useLocation();
  const navigate = useNavigate();

  const [mode, setMode] = useState('login');
  const [step, setStep] = useState('phone');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const returnPath = resolveReturnPath(location.state?.from);

  if (loggedIn) {
    return <Navigate to={returnPath} replace />;
  }

  const digits = normalizeMobile(mobile);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValidIndianMobile(digits)) {
      setError('Enter a valid 10-digit Indian mobile number.');
      return;
    }
    if (mode === 'signup' && !name.trim()) {
      setError('Enter your name.');
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        await pilotAuthRegister({ name: name.trim(), mobile: digits });
      } else {
        await pilotAuthLogin({ mobile: digits });
      }
      setStep('otp');
      setOtp('');
    } catch (err) {
      const msg = err instanceof V2ApiError ? err.message : 'Something went wrong. Try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    const code = String(otp).trim();
    if (!/^\d{4,8}$/.test(code)) {
      setError('Enter the OTP you received.');
      return;
    }
    setSubmitting(true);
    try {
      const data = await pilotAuthValidateOtp({ mobile: digits, otp: code });
      const prev = (await getCachedSession()) || {};
      await login({
        ...prev,
        mobile: digits,
        token: data.token,
        user: data.user,
      });
      if (mode === 'signup') {
        trackGa4SignUp();
      } else {
        trackGa4Login();
      }
      navigate(returnPath, { replace: true });
    } catch (err) {
      const msg = err instanceof V2ApiError ? err.message : 'Invalid or expired OTP.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const goBackToPhone = () => {
    setStep('phone');
    setOtp('');
    setError('');
  };

  return (
    <PageContainer title={mode === 'signup' ? 'Create account' : 'Sign in'}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {step === 'phone'
          ? 'We’ll send a one-time code to your mobile number.'
          : `Enter the code sent to +91 ${digits}.`}
      </Typography>

      {step === 'phone' && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            type="button"
            variant={mode === 'login' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => {
              setMode('login');
              setError('');
            }}
            sx={{ borderRadius: '5px' }}
          >
            Login
          </Button>
          <Button
            type="button"
            variant={mode === 'signup' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => {
              setMode('signup');
              setError('');
            }}
            sx={{ borderRadius: '5px' }}
          >
            Sign up
          </Button>
        </Box>
      )}

      {step === 'phone' ? (
        <Box component="form" onSubmit={handleRequestOtp} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {mode === 'signup' && (
            <TextField
              label="Name"
              autoComplete="name"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              error={Boolean(error && !name.trim())}
              fullWidth
            />
          )}
          <TextField
            label="Mobile number"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="10-digit number"
            value={mobile}
            onChange={(ev) => setMobile(ev.target.value)}
            error={Boolean(error)}
            helperText={error || undefined}
            fullWidth
          />
          <Button type="submit" fullWidth size="large" disabled={submitting} sx={{ borderRadius: '5px' }}>
            {submitting ? 'Sending code…' : 'Send OTP'}
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleVerifyOtp} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="OTP"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="Enter OTP"
            value={otp}
            onChange={(ev) => setOtp(ev.target.value.replace(/\D/g, '').slice(0, 8))}
            error={Boolean(error)}
            helperText={error || undefined}
            fullWidth
            autoFocus
          />
          <Button type="submit" fullWidth size="large" disabled={submitting} sx={{ borderRadius: '5px' }}>
            {submitting ? 'Verifying…' : 'Verify & continue'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            <Link component="button" type="button" underline="hover" onClick={goBackToPhone} sx={{ cursor: 'pointer' }}>
              Change number
            </Link>
          </Typography>
        </Box>
      )}
    </PageContainer>
  );
}
